// frontend/src/components/shared/ToastNotification.tsx
import { useEffect } from 'react';
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  Info,
  X,
  type LucideIcon,
} from 'lucide-react';

export type ToastType = 'success' | 'warning' | 'error' | 'info';

export interface ToastNotificationProps {
  type?: ToastType;
  title?: string;
  message?: string;
  subMessage?: string;
  autoDismiss?: boolean;
  duration?: number;
  onClose?: () => void;
  show?: boolean;
}

interface ToastTypeConfig {
  icon: LucideIcon;
  bgColor: string;
  borderColor: string;
  iconColor: string;
  titleColor: string;
  messageColor: string;
  subMessageColor: string;
  closeHoverColor: string;
  ringColor: string;
  ringOffsetColor: string;
}

const ToastNotification = ({
  type = 'info',
  title,
  message,
  subMessage,
  autoDismiss = true,
  duration = 5000,
  onClose,
  show = true,
}: ToastNotificationProps) => {
  useEffect(() => {
    if (show && autoDismiss && duration > 0) {
      const timer = setTimeout(() => {
        onClose?.();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [show, autoDismiss, duration, onClose]);

  if (!show) return null;

  const typeConfig: Record<ToastType, ToastTypeConfig> = {
    success: {
      icon: CheckCircle,
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      iconColor: 'text-green-600',
      titleColor: 'text-green-800',
      messageColor: 'text-green-700',
      subMessageColor: 'text-green-600',
      closeHoverColor: 'hover:text-green-500',
      ringColor: 'focus:ring-green-500',
      ringOffsetColor: 'focus:ring-offset-green-50',
    },
    warning: {
      icon: AlertTriangle,
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      iconColor: 'text-amber-600',
      titleColor: 'text-amber-800',
      messageColor: 'text-amber-700',
      subMessageColor: 'text-amber-600',
      closeHoverColor: 'hover:text-amber-500',
      ringColor: 'focus:ring-amber-500',
      ringOffsetColor: 'focus:ring-offset-amber-50',
    },
    error: {
      icon: XCircle,
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      iconColor: 'text-red-600',
      titleColor: 'text-red-800',
      messageColor: 'text-red-700',
      subMessageColor: 'text-red-600',
      closeHoverColor: 'hover:text-red-500',
      ringColor: 'focus:ring-red-500',
      ringOffsetColor: 'focus:ring-offset-red-50',
    },
    info: {
      icon: Info,
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      iconColor: 'text-blue-600',
      titleColor: 'text-blue-800',
      messageColor: 'text-blue-700',
      subMessageColor: 'text-blue-600',
      closeHoverColor: 'hover:text-blue-500',
      ringColor: 'focus:ring-blue-500',
      ringOffsetColor: 'focus:ring-offset-blue-50',
    },
  };

  const config = typeConfig[type] || typeConfig.info;
  const Icon = config.icon;

  return (
    <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
      <div
        className={`${config.bgColor} ${config.borderColor} border rounded-lg shadow-lg p-4 max-w-md`}
      >
        <div className="flex items-start">
          <Icon className={`h-5 w-5 ${config.iconColor} mt-0.5 flex-shrink-0`} />
          <div className="ml-3 flex-1">
            {title && (
              <h3 className={`text-sm font-medium ${config.titleColor}`}>{title}</h3>
            )}
            {message && (
              <p className={`${title ? 'mt-1' : ''} text-sm ${config.messageColor}`}>
                {message}
              </p>
            )}
            {subMessage && (
              <p className={`mt-2 text-xs ${config.subMessageColor}`}>{subMessage}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className={`ml-3 flex-shrink-0 inline-flex ${config.iconColor} ${config.closeHoverColor} focus:outline-none focus:ring-2 ${config.ringColor} focus:ring-offset-2 ${config.ringOffsetColor} rounded`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ToastNotification;
